import React, { useEffect, useRef, useState } from 'react';

const members = [
  {
    name: 'Rohit Jain',
    initials: 'RJ',
    color: '#c0ff00',
    role: 'SDE-2 @ Amazon',
    faangBadge: 'Amazon',
    oneliner: '200+ interviews conducted as interviewer at Amazon. He knows exactly what gets you hired.',
  },
  {
    name: 'Suresh Gandhi',
    initials: 'SG',
    color: '#60a5fa',
    role: 'SDE-2 @ Microsoft · Ex-Amazon',
    faangBadge: 'Microsoft · Amazon',
    oneliner: 'Distributed systems engineer with FAANG experience at both Amazon and Microsoft.',
  },
  {
    name: 'Shubham Chandak',
    initials: 'SC',
    color: '#a78bfa',
    role: 'Software Engineer @ Bloomberg',
    faangBadge: 'Bloomberg',
    oneliner: 'Builds large-scale financial systems. Designs curriculum that makes complex ideas simple.',
  },
];

const chapters = [
  {
    year: '2014',
    eyebrow: 'Where It Started',
    heading: 'Three Roommates. One Hostel. Endless Late Nights.',
    body: "We met as undergraduates — crammed into the same hostel, staying up arguing about code, trading notes, and slowly becoming each other's closest friends. We didn't know it then, but those conversations were the foundation of everything we'd build together.",
    accent: '#c0ff00',
    icon: '🏠',
    tag: 'College',
  },
  {
    year: '2018',
    eyebrow: 'The Real World',
    heading: 'We Ended Up On The Other Side Of The Interview Table.',
    body: "Life after college pulled us to different cities. Amazon. Microsoft. Bloomberg. Between us, we've designed systems serving hundreds of millions of users and conducted hundreds of technical interviews. We saw from the inside exactly what made candidates succeed — and what made them fail.",
    accent: '#60a5fa',
    icon: '💼',
    tag: 'Industry',
  },
  {
    year: '2021',
    eyebrow: 'The Frustration',
    heading: 'Good Engineers Were Failing Interviews They Deserved To Pass.',
    body: "That bothered us. Smart, capable engineers freezing up in system design rounds — not because they lacked ability, but because nobody had given them a structured way to think. The resources out there were too vague, too theoretical, or just someone reading off a whiteboard.",
    accent: '#a78bfa',
    icon: '💡',
    tag: 'Insight',
  },
  {
    year: '2023',
    eyebrow: 'Sweet Codey',
    heading: "So We Built What We Wished We'd Had.",
    body: "We got back in a room — virtually this time — and poured everything we knew into a course. Not a textbook. A practical, visual, deeply structured system any engineer could use in their next interview. Every lesson went through one filter: would this actually help someone get the job?",
    accent: '#f472b6',
    icon: '🚀',
    tag: 'Launch',
  },
  {
    year: '2025',
    eyebrow: 'What Drives Us',
    heading: "We're Not Here To Teach. We're Here To Change Careers.",
    body: "23,000 engineers later, that's still the thing that gets us out of bed. Not the ratings — those 'I got the offer' messages. The engineers who went from self-doubt to senior roles. The ones who cracked Amazon, Google, Microsoft after months of trying. That's why we keep going.",
    accent: '#34d399',
    icon: '🎯',
    tag: 'Today',
  },
];

const Team = () => {
  const [active, setActive] = useState(0);
  const [direction, setDirection] = useState('next'); // 'next' | 'prev'
  const [animKey, setAnimKey] = useState(0);
  const [visibleCards, setVisibleCards] = useState([]);
  const cardRefs = useRef([]);

  const select = (i) => {
    setDirection(i > active ? 'next' : 'prev');
    setActive(i);
    setAnimKey(k => k + 1);
  };

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const idx = cardRefs.current.indexOf(entry.target);
          if (idx !== -1 && entry.isIntersecting)
            setVisibleCards(prev => prev.includes(idx) ? prev : [...prev, idx]);
        });
      },
      { threshold: 0.15 }
    );
    cardRefs.current.forEach(c => { if (c) obs.observe(c); });
    return () => obs.disconnect();
  }, []);

  const ch = chapters[active];

  return (
    <section className="team" id="team">

      {/* Story */}
      <div className="team-story">
        <div className="section-header">
          <div className="section-badge">Our Story</div>
          <h2 className="section-title">
            Built By Engineers Who've<br /><span className="text-gradient">Been On Both Sides.</span>
          </h2>
        </div>

        {/* Time Machine */}
        <div className="tm-wrap">

          {/* Year selector — horizontal timeline bar */}
          <div className="tm-bar">
            <div className="tm-bar-line" />
            <div
              className="tm-bar-fill"
              style={{ width: `${(active / (chapters.length - 1)) * 100}%` }}
            />
            {chapters.map((c, i) => (
              <button
                key={i}
                className={`tm-stop ${active === i ? 'tm-stop-active' : ''}`}
                style={{ '--c': c.accent }}
                onClick={() => select(i)}
              >
                <div className="tm-stop-dot" />
                <span className="tm-stop-year">{c.year}</span>
                <span className="tm-stop-tag">{c.tag}</span>
              </button>
            ))}
          </div>

          {/* Content panel */}
          <div className={`tm-panel tm-panel-${direction}`} key={animKey} style={{ '--c': ch.accent }}>
            <div className="tm-panel-left">
              <div className="tm-icon-wrap">
                <span className="tm-icon">{ch.icon}</span>
              </div>
              <div>
                <span className="tm-eyebrow">{ch.eyebrow}</span>
                <h3 className="tm-heading">{ch.heading}</h3>
              </div>
            </div>
            <div className="tm-panel-right">
              <p className="tm-body">{ch.body}</p>
              <div className="tm-nav-btns">
                {active > 0 && (
                  <button className="tm-nav-btn" onClick={() => select(active - 1)}>← {chapters[active - 1].year}</button>
                )}
                {active < chapters.length - 1 && (
                  <button className="tm-nav-btn tm-nav-btn-next" style={{ '--c': chapters[active + 1].accent }} onClick={() => select(active + 1)}>
                    {chapters[active + 1].year} →
                  </button>
                )}
              </div>
            </div>
            <div className="tm-panel-bar" />
          </div>

        </div>

        {/* Quote */}
        <div className="story-quote-block">
          <div className="story-quote-inner">
            <div className="story-quote-text">
              "If you can't explain it simply,<br />you don't understand it well enough."
            </div>
            <div className="story-quote-attr">— Albert Einstein, and the principle we build every lesson around</div>
          </div>
          <div className="story-stat-row">
            <div className="story-stat"><span>200+</span>interviews conducted</div>
            <div className="story-stat-sep" />
            <div className="story-stat"><span>FAANG</span>insiders teaching you</div>
            <div className="story-stat-sep" />
            <div className="story-stat"><span>16+</span>years in the industry</div>
          </div>
        </div>
      </div>

      {/* Team */}
      <div className="team-members-wrap">
        <div className="team-members-label">The People Behind Sweet Codey</div>
        <div className="team-grid">
          {members.map((member, index) => (
            <div
              key={index}
              ref={el => (cardRefs.current[index] = el)}
              className={`team-card ${visibleCards.includes(index) ? 'visible' : ''}`}
              style={{ '--member-color': member.color, transitionDelay: `${index * 0.1}s` }}
            >
              <div className="team-avatar" style={{ background: `${member.color}15`, color: member.color, border: `1.5px solid ${member.color}35` }}>
                {member.initials}
              </div>
              <div className="team-info">
                <div className="team-name">{member.name}</div>
                <div className="team-faang-badge" style={{ color: member.color, borderColor: `${member.color}55`, background: `${member.color}12` }}>
                  🏢 {member.faangBadge}
                </div>
                <div className="team-role">{member.role}</div>
                <div className="team-oneliner">{member.oneliner}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="team-cta-block">
        <a href="#courses" className="btn btn-primary team-cta-btn">
          Learn From FAANG Insiders
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
            <path d="M3 8h10m0 0l-4-4m4 4l-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </a>
        <p className="team-cta-sub">Join 23,000+ engineers · 30-day money-back guarantee</p>
      </div>

    </section>
  );
};

export default Team;
